import styled from 'styled-components';
import type { NavItemType } from '../../types/navigation';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Images } from '../../assets';
import { useCallback } from 'react';
import { useLoading } from '@/context/LoadingContenxt';
import { postLogout } from '@/api/auth/postLogout';
import { useAuth } from '@/context/AuthContext';
import { useTranslation } from 'react-i18next';

const Navbar = () => {
  const location = useLocation();
  const { startLoading, stopLoading } = useLoading();
  const { logout } = useAuth();
  const { t } = useTranslation();

  const navItems: NavItemType[] = [
    {
      label: t('navbar.playerList'),
      path: '/player-list',
    },
    {
      label: t('navbar.partnerList'),
      path: '/partner/center-list',
      children: [
        {
          label: t('navbar.r2CenterList'),
          path: '/partner/center-list',
        },
        {
          label: t('navbar.r2PlannerList'),
          path: '/partner/planner-list',
        },
      ],
    },
    {
      label: t('navbar.signUp'),
      path: '/sign-up/player',
      children: [
        {
          label: t('navbar.playerSignUp'),
          path: '/sign-up/player',
        },
        {
          label: t('navbar.centreSignUp'),
          path: '/sign-up/center',
        },
      ],
    },
    {
      label: t('navbar.r2LicenseKeyManagement'),
      path: '/license-management',
    },
  ];

  const isActive = (item: NavItemType) => {
    const currentPath = location.pathname;

    if (item.children) {
      return item.children.some(
        (child) =>
          currentPath === child.path || currentPath.startsWith(`${child.path}/`)
      );
    }

    return currentPath === item.path || currentPath.startsWith(`${item.path}/`);
  };

  const onClickLogout = useCallback(async () => {
    console.log('?');
    startLoading();
    try {
      const res = await postLogout();
      if (res.isSuccess) {
        logout();
      } else {
      }
    } finally {
      stopLoading();
    }
  }, [logout, startLoading, stopLoading]);

  return (
    <Container>
      <NavList>
        {navItems.map((item) => {
          return (
            <NavItem key={`nav-item-${item.label}`}>
              <NavLink to={item.path} $active={isActive(item)}>
                {item.label}
              </NavLink>
              {item.children && item.children.length > 0 && (
                <SubNavList>
                  {item.children.map((child) => (
                    <SubNavItem key={`sub-nav-item-${child.label}`}>
                      <NavLink to={child.path} $active={isActive(child)}>
                        {child.label}
                      </NavLink>
                    </SubNavItem>
                  ))}
                </SubNavList>
              )}
            </NavItem>
          );
        })}
      </NavList>
      <LogoutSection onClick={onClickLogout}>
        <LogoutIcon src={Images.LogoutImg} alt="logout" />
        <div>{t('navbar.logout')}</div>
      </LogoutSection>
    </Container>
  );
};

export default Navbar;

const Container = styled.div`
  box-sizing: border-box;
  background-color: white;
  border-right: 1px solid var(--color-border, #ddd);
  width: 240px;
  min-width: 240px;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 24px 16px;
`;

const NavList = styled.div`
  margin: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const NavItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const NavLink = styled(Link)<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  padding: 12px 14px;
  border-radius: 8px;
  text-decoration: none;
  background: ${({ $active }) => ($active ? '#dbeafe' : 'transparent')};
  color: ${({ $active }) => ($active ? '#1d4ed8' : '#374151')};
  font-size: 14px;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  transition: all 0.2s;

  &:hover {
    background: #eff6ff;
    color: #2563eb;
  }
`;

const SubNavList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-left: 16px;
  border-left: 2px solid #e5e7eb;
`;

const SubNavItem = styled.div`
  margin-top: 4px;
`;

const LogoutSection = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;

  width: 100%;
  padding: 12px 14px;
  box-sizing: border-box;

  border: none;
  background: transparent;
  border-radius: 8px;

  cursor: pointer;

  font-size: 14px;
  color: #6b7280;

  transition: all 0.2s;

  &:hover {
    background: #f9fafb;
    color: #111827;
  }
`;

const LogoutIcon = styled.img`
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  object-fit: contain;
`;
